// "Enhance ruler" display filter for ground-truth camera photos.
//
// A steel/glossy ruler or uneven lighting leaves one end of the ruler dim and
// low-contrast, and at ~5 px/mm the millimetre ticks come out as faint smudges.
// Global camera settings can't fix that (top and bottom need opposite
// adjustments), so this works on the saved photo, per region:
//   mono -> 3x3 median (kills the colour speckle) -> flatten the lighting
//   (divide by a very blurry copy) -> stretch contrast -> unsharp mask.
// Prototyped and tuned on a real 864x1536 capture (tick separability up ~3-5x)
// before being ported here. It is a DISPLAY filter only: the stored photo is
// never changed, and it cannot add detail the sensor did not capture - it makes
// ticks that are already there countable. Pixel positions do not move.

// Tuned values (sigma/percent as in the Python prototype).
const FLATTEN_RADIUS = 60; // box radius, 3 passes ~ gaussian sigma 60 px
const SHARPEN_RADIUS = 1; // box radius, 3 passes ~ gaussian sigma 1.4 px
const SHARPEN_AMOUNT = 2.2;
const STRETCH_LOW = 0.005;
const STRETCH_HIGH = 0.995;

function clampIndex(i, max) {
  return i < 0 ? 0 : i > max ? max : i;
}

function boxBlurPass(src, dst, width, height, radius, horizontal) {
  const length = horizontal ? width : height;
  const lines = horizontal ? height : width;
  const stride = horizontal ? 1 : width;
  const lineStride = horizontal ? width : 1;
  const k = 2 * radius + 1;
  for (let line = 0; line < lines; line++) {
    const base = line * lineStride;
    let acc = 0;
    for (let i = -radius; i <= radius; i++) acc += src[base + clampIndex(i, length - 1) * stride];
    for (let i = 0; i < length; i++) {
      dst[base + i * stride] = acc / k;
      acc +=
        src[base + clampIndex(i + radius + 1, length - 1) * stride] -
        src[base + clampIndex(i - radius, length - 1) * stride];
    }
  }
}

// Repeated box blurs approximate a gaussian (3 passes is plenty here).
export function blur(luma, width, height, radius, passes = 3) {
  let a = luma;
  let b = new Float32Array(luma.length);
  for (let p = 0; p < passes; p++) {
    boxBlurPass(a, b, width, height, radius, true);
    const c = new Float32Array(luma.length);
    boxBlurPass(b, c, width, height, radius, false);
    a = c;
  }
  return a;
}

export function toLuma(data) {
  const out = new Float32Array(data.length / 4);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    out[j] = (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]) / 255;
  }
  return out;
}

export function median3(luma, width, height) {
  const out = new Float32Array(luma.length);
  const w = new Float32Array(9);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      let n = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = clampIndex(y + dy, height - 1) * width;
        for (let dx = -1; dx <= 1; dx++) w[n++] = luma[yy + clampIndex(x + dx, width - 1)];
      }
      // insertion sort of 9 values
      for (let i = 1; i < 9; i++) {
        const v = w[i];
        let j = i - 1;
        while (j >= 0 && w[j] > v) {
          w[j + 1] = w[j];
          j--;
        }
        w[j + 1] = v;
      }
      out[y * width + x] = w[4];
    }
  }
  return out;
}

// Divide by a very blurry copy so a dim end and a bright end end up equally bright.
export function flattenLighting(luma, width, height, radius = FLATTEN_RADIUS) {
  const bg = blur(luma, width, height, Math.min(radius, Math.floor(Math.min(width, height) / 2) - 1));
  let sum = 0;
  for (let i = 0; i < bg.length; i++) sum += bg[i];
  const mean = sum / bg.length;
  const out = new Float32Array(luma.length);
  for (let i = 0; i < luma.length; i++) {
    const v = luma[i] * (mean / (bg[i] + 1e-3));
    out[i] = v < 0 ? 0 : v > 1 ? 1 : v;
  }
  return out;
}

export function stretchContrast(luma, low = STRETCH_LOW, high = STRETCH_HIGH) {
  const hist = new Uint32Array(256);
  for (let i = 0; i < luma.length; i++) hist[Math.min(255, Math.max(0, Math.round(luma[i] * 255)))]++;
  const cut = (fraction) => {
    const target = fraction * luma.length;
    let acc = 0;
    for (let v = 0; v < 256; v++) {
      acc += hist[v];
      if (acc >= target) return v / 255;
    }
    return 1;
  };
  const lo = cut(low);
  const hi = cut(high);
  const span = hi - lo;
  const out = new Float32Array(luma.length);
  if (span < 1e-3) {
    out.set(luma); // flat image: nothing to stretch
    return out;
  }
  for (let i = 0; i < luma.length; i++) {
    const v = (luma[i] - lo) / span;
    out[i] = v < 0 ? 0 : v > 1 ? 1 : v;
  }
  return out;
}

export function unsharp(luma, width, height, radius = SHARPEN_RADIUS, amount = SHARPEN_AMOUNT) {
  const soft = blur(luma, width, height, radius);
  const out = new Float32Array(luma.length);
  for (let i = 0; i < luma.length; i++) {
    const v = luma[i] + amount * (luma[i] - soft[i]);
    out[i] = v < 0 ? 0 : v > 1 ? 1 : v;
  }
  return out;
}

// RGBA pixels in, enhanced mono RGBA pixels out ({ data, width, height }).
export function enhanceRuler({ data, width, height }) {
  let luma = toLuma(data);
  luma = median3(luma, width, height);
  luma = flattenLighting(luma, width, height);
  luma = stretchContrast(luma);
  luma = unsharp(luma, width, height);
  const out = new Uint8ClampedArray(data.length);
  for (let i = 0, j = 0; j < luma.length; i += 4, j++) {
    const v = Math.round(luma[j] * 255);
    out[i] = v;
    out[i + 1] = v;
    out[i + 2] = v;
    out[i + 3] = 255;
  }
  return { data: out, width, height };
}

// Browser-only wrapper: Blob (JPEG) in, enhanced PNG Blob out. Kept apart from
// the pixel functions above so those stay testable without a canvas.
export async function enhanceImageBlob(blob) {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error("Could not decode the image."));
      element.src = objectUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("This browser can't process images.");
    context.drawImage(image, 0, 0);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    const enhanced = enhanceRuler({ data: pixels.data, width: canvas.width, height: canvas.height });
    context.putImageData(new ImageData(enhanced.data, canvas.width, canvas.height), 0, 0);
    return await new Promise((resolve, reject) =>
      canvas.toBlob((result) => (result ? resolve(result) : reject(new Error("Could not encode the image."))), "image/png")
    );
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
